import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'

import { ExportCertificates } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ExportCertificate'
import { useExportCertificatesModal } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ExportCertificate/useExportCertificatesModal'
import { useManualExportCertificatesModal } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ExportCertificate/useManualExportCertificatesModal'
import {
  BulkSelectionFilters,
  useBulkAction,
} from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useBulkActionsConfig'
import {
  ColumnConfig,
  useColumns,
} from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useColumns'
import {
  TrainingParticipantsMappedVariables,
  useDataSource,
} from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useDataSource'
import { ImportCertificates } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ImportCertificates'
import { useImportCertificatesModal } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ImportCertificates/useImportCertificatesModal'
import {
  CompletionConfiguration,
  DetailOutletContext,
  TrainingsTraining,
  TrainingsTrainingClass,
} from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

const ParticipantsOneDataTable = ({
  training,
  trainingClasses,
  completionConfiguration,
  outletContext,
}: {
  training: TrainingsTraining
  trainingClasses: TrainingsTrainingClass[]
  completionConfiguration: CompletionConfiguration
  outletContext: DetailOutletContext
}) => {
  const { hasBulkImportCertificates } = outletContext

  const columnConfig = useMemo((): ColumnConfig => {
    const isSessionsRequired = !!(
      completionConfiguration?.attendancePercentage ||
      (!completionConfiguration?.attendancePercentage &&
        !completionConfiguration?.moduleCompletedPercentage &&
        !completionConfiguration?.knowledgeTestRequired)
    )

    return {
      isSessionsRequired,
      isModulesRequired: !!completionConfiguration?.moduleCompletedPercentage,
      knowledgeTestRequired: !!completionConfiguration?.knowledgeTestRequired,
      courseValidityEnabled: !!training.validFor,
    }
  }, [completionConfiguration, training.validFor])

  const bulkExportModal = useExportCertificatesModal()
  const manualExportModal = useManualExportCertificatesModal()
  const bulkImportModal = useImportCertificatesModal()

  const source = useDataSource(training, trainingClasses, manualExportModal.open, {
    bulkImportEnabled: hasBulkImportCertificates,
  })
  const columns = useColumns(columnConfig)

  const currentFilters = useMemo((): BulkSelectionFilters | undefined => {
    const vars: TrainingParticipantsMappedVariables | undefined = source.mappedVariables
    if (!vars) return undefined

    const filter = vars.filter

    return {
      search: vars.search ?? undefined,
      classId: filter?.classId?.in?.[0] ?? undefined,
      status: filter?.status?.in?.[0] ?? undefined,
      teamId: filter?.teamId?.in?.[0] ?? undefined,
      dueDate: filter?.dueDate?.in?.[0] ?? undefined,
    }
  }, [source.mappedVariables])

  const handleBulkAction = useBulkAction(
    trainingClasses,
    bulkExportModal.exportCertificatesHandlers,
    hasBulkImportCertificates ? bulkImportModal.importCertificatesHandlers : undefined,
    currentFilters
  )

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns,
            },
          },
        ]}
      />
      {bulkExportModal.isOpen && (
        <ExportCertificates
          onClose={bulkExportModal.close}
          trainingId={training.id}
          trainingMemberships={outletContext.members}
          initialSelection={bulkExportModal.selection}
          initialSelectedEmployeeIds={bulkExportModal.selectedEmployeeIds}
        />
      )}
      {manualExportModal.isOpen && (
        <ExportCertificates
          onClose={manualExportModal.close}
          trainingId={training.id}
          trainingMemberships={outletContext.members}
        />
      )}
      {bulkImportModal.isOpen && (
        <ImportCertificates
          onClose={bulkImportModal.close}
          trainingId={training.id}
          participants={bulkImportModal.participants}
        />
      )}
      <Outlet context={outletContext} />
    </>
  )
}

export default ParticipantsOneDataTable
